import { CalendarIcon } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"

const SPANISH_MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
]

function formatDateSpanish(date: Date): string {
  const day = date.getDate()
  const month = SPANISH_MONTHS[date.getMonth()]
  const year = date.getFullYear()
  return `${day} de ${month} de ${year}`
}

function parseISODate(iso: string): Date | undefined {
  if (!iso) return undefined
  const [year, month, day] = iso.split("-").map(Number)
  if (!year || !month || !day) return undefined
  return new Date(year, month - 1, day)
}

function toISODate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

interface DatePickerProps {
  label: string
  value: string
  onChange: (iso: string) => void
  error?: string
  placeholder?: string
  minDate?: string
  maxDate?: string
}

export default function DatePicker({
  label,
  value,
  onChange,
  error,
  placeholder = "Seleccionar fecha...",
  minDate,
  maxDate,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const selected = parseISODate(value)
  const isMobile = useIsMobile()

  const today = new Date()
  const fromDate = minDate
    ? parseISODate(minDate)!
    : new Date(today.getFullYear() - 50, 0)
  const toDate = maxDate
    ? parseISODate(maxDate)!
    : new Date(today.getFullYear() - 10, 11)

  const defaultMonth = selected ?? toDate

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(toISODate(date))
      setOpen(false)
    }
  }

  const triggerButton = (
    <Button
      variant="outline"
      className={cn(
        "w-full justify-start text-left font-normal",
        !selected && "text-muted-foreground",
      )}
    >
      <CalendarIcon className="mr-2 size-4" />
      {selected ? formatDateSpanish(selected) : placeholder}
    </Button>
  )

  const calendar = (
    <Calendar
      mode="single"
      captionLayout="dropdown"
      selected={selected}
      defaultMonth={defaultMonth}
      onSelect={handleSelect}
      startMonth={fromDate}
      endMonth={toDate}
      formatters={{
        formatMonthDropdown: (date) => SPANISH_MONTHS[date.getMonth()],
      }}
    />
  )

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {isMobile ? (
        <Sheet open={open} onOpenChange={setOpen}>
          <div onClick={() => setOpen(true)}>{triggerButton}</div>
          <SheetContent side="bottom" className="px-0">
            <SheetHeader className="px-4">
              <SheetTitle>{label}</SheetTitle>
            </SheetHeader>
            <div className="flex justify-center pb-4">{calendar}</div>
          </SheetContent>
        </Sheet>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            {calendar}
          </PopoverContent>
        </Popover>
      )}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  )
}
